import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { applyApiErrorsToForm, isApiErrorEnvelope } from '@/api/form-errors'
import { useLogin, useResendMfa, useVerifyMfa } from '@/auth/api'
import { loginSchema, mfaSchema, type LoginFormValues, type MfaFormValues } from '@/auth/schemas'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import type { AdminAuthUser } from '@/contexts/auth-context'
import { useAuth } from '@/contexts/use-auth'

interface ErrorBanner {
  message: string
  traceId?: string
}

interface LocationState {
  from?: { pathname: string }
}

interface VerifyResponse {
  data?: AdminAuthUser
  token?: string
}

/**
 * Admin login (DEC-ADMIN-011: email-2FA). Two steps in a single screen:
 *  1. credentials — email + password → backend emails a 6-digit code;
 *  2. mfa — enter the code → exchange it for the bearer.
 *
 * The MFA step stays UNAUTHENTICATED (the challenge handle lives in component
 * state only, never localStorage) so RequireUnauth keeps us on /auth/login
 * until the second factor passes.
 */
export function LoginForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { setSession } = useAuth()

  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials')
  const [challenge, setChallenge] = useState<string | null>(null)
  const [errorBanner, setErrorBanner] = useState<ErrorBanner | null>(null)
  const [infoBanner, setInfoBanner] = useState<string | null>(null)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })
  const mfaForm = useForm<MfaFormValues>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  })

  const login = useLogin()
  const verify = useVerifyMfa()
  const resend = useResendMfa()

  const fromState = location.state as LocationState | null
  const redirectTo = fromState?.from?.pathname ?? '/'

  function onSubmitCredentials(values: LoginFormValues) {
    setErrorBanner(null)
    setInfoBanner(null)
    login.mutate(values, {
      onSuccess: (response) => {
        const handle = (response as { challenge?: string }).challenge
        if (!handle) {
          setErrorBanner({ message: t('common.error') })
          return
        }
        setChallenge(handle)
        setStep('mfa')
        mfaForm.reset()
      },
      onError: (error) => {
        const result = applyApiErrorsToForm(error, form.setError)
        if (result.handled) return

        if (result.code === 'unauthenticated') {
          setErrorBanner({ message: t('auth.login.invalidCredentials'), traceId: result.traceId })
        } else if (result.code === 'forbidden') {
          // DEC-AUTH-005: credential matched but admin is disabled — the 403
          // only fires AFTER password verification, so this is not enumeration.
          setErrorBanner({ message: t('auth.login.accountDisabled'), traceId: result.traceId })
        } else if (result.code === 'rate_limited') {
          setErrorBanner({ message: t('auth.login.rateLimited'), traceId: result.traceId })
        } else {
          setErrorBanner({ message: result.message ?? t('common.error'), traceId: result.traceId })
        }
      },
    })
  }

  function onSubmitMfa(values: MfaFormValues) {
    if (!challenge) return
    setErrorBanner(null)
    setInfoBanner(null)
    verify.mutate(
      { challenge, code: values.code },
      {
        onSuccess: (response) => {
          const { data: authUser, token } = response as VerifyResponse
          if (!token || !authUser) {
            setErrorBanner({ message: t('common.error') })
            return
          }
          setSession({ token, authUser })
          navigate(redirectTo, { replace: true })
        },
        onError: (error) => handleMfaError(error),
      },
    )
  }

  function handleMfaError(error: unknown) {
    // An expired / unknown challenge → bounce back to the credentials step.
    if (isApiErrorEnvelope(error) && error.code === 'validation_failed') {
      const fieldErrors = (error.details as { errors?: Record<string, string[]> }).errors ?? {}
      if (fieldErrors.challenge) {
        backToCredentials()
        setErrorBanner({ message: t('auth.mfa.sessionExpired'), traceId: error.trace_id })
        return
      }
    }

    const result = applyApiErrorsToForm(error, mfaForm.setError)
    if (result.handled) return

    if (result.code === 'rate_limited') {
      setErrorBanner({ message: t('auth.login.rateLimited'), traceId: result.traceId })
    } else {
      setErrorBanner({ message: result.message ?? t('common.error'), traceId: result.traceId })
    }
  }

  function onResend() {
    if (!challenge) return
    setErrorBanner(null)
    setInfoBanner(null)
    resend.mutate(
      { challenge },
      {
        onSuccess: (response) => {
          const next = (response as { challenge?: string }).challenge
          if (next) setChallenge(next)
          mfaForm.reset()
          setInfoBanner(t('auth.mfa.resent'))
        },
        onError: (error) => handleMfaError(error),
      },
    )
  }

  function backToCredentials() {
    setStep('credentials')
    setChallenge(null)
    mfaForm.reset()
    setInfoBanner(null)
  }

  return (
    <div className="flex flex-col gap-4">
      {errorBanner && (
        <Alert variant="destructive">
          <AlertDescription>
            {errorBanner.message}
            {errorBanner.traceId && (
              <span className="block text-xs opacity-70">trace ID: {errorBanner.traceId}</span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {infoBanner && (
        <Alert>
          <AlertDescription>{infoBanner}</AlertDescription>
        </Alert>
      )}

      {step === 'credentials' ? (
        <Form key="credentials" {...form}>
          <form onSubmit={form.handleSubmit(onSubmitCredentials)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.login.emailLabel')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="email" inputMode="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.login.passwordLabel')}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={login.isPending} className="mt-2">
              {login.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>
          </form>
        </Form>
      ) : (
        <Form key="mfa" {...mfaForm}>
          <form onSubmit={mfaForm.handleSubmit(onSubmitMfa)} className="flex flex-col gap-4">
            <p className="text-sm opacity-80">{t('auth.mfa.intro')}</p>

            <FormField
              control={mfaForm.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.mfa.codeLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={verify.isPending} className="mt-2">
              {verify.isPending ? t('auth.mfa.submitting') : t('auth.mfa.submit')}
            </Button>

            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="underline disabled:opacity-50"
                onClick={onResend}
                disabled={resend.isPending}
              >
                {resend.isPending ? t('auth.mfa.resending') : t('auth.mfa.resend')}
              </button>
              <button type="button" className="underline" onClick={backToCredentials}>
                {t('auth.mfa.back')}
              </button>
            </div>
          </form>
        </Form>
      )}
    </div>
  )
}
